import { useMentorProfileReviewByMentorId } from "@/lib/api/mentor-profile";
import { useUserRoles } from "@/lib/api/roles";
import { useCurrentUser, useGetUserSupervisorMentors, useUser } from "@/lib/api/user";
import { pendingProfileChangeCount } from "@/lib/mentor-profile/revision-change-detection";
import { isMentorIdentity } from "@/lib/utils/system-roles";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromUser } from "@/lib/casl/privileged-access";
import { useFeatureFlagEnabled } from "@posthog/react";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { liveProfileDraft } from "./matching-profile-source";
import { RecordPageLoader } from "./record-page-loader";
import { UserRecordShell } from "./user-record-shell";
import { UserRecordTabContent } from "./user-record-tab-content";
import {
  defaultTabId,
  getRecordTabs,
  shouldShowSupervision,
  type UserRecordTabId,
} from "./user-record-tabs";

interface UserRecordPageProps {
  userId: string;
  initialTabId?: UserRecordTabId;
}

export function UserRecordPage({
  userId,
  initialTabId,
}: UserRecordPageProps) {
  const { from } = useSearch({ from: "/_authenticated/users/$userId" });
  const back =
    from === "care-reviews"
      ? { to: "/care-reviews" as const, label: "Back to Care Reviews" }
      : { to: "/users" as const, label: "Users" };
  const { data: user, isPending, isError } = useUser(userId);
  const { data: assignedRoles } = useUserRoles(userId);
  const { data: currentUser } = useCurrentUser();
  const ability = useAbility();
  const hasPrivilegedAccess = hasPrivilegedAccessFromUser(currentUser ?? null);
  const canViewRoles = hasPrivilegedAccess && ability.can("read", "roles");
  const canReadMentorships =
    hasPrivilegedAccess && ability.can("read", "mentorships");
  const canManageSupervisorAssignments =
    hasPrivilegedAccess &&
    (ability.can("assign", "supervisor") ||
      ability.can("unassign", "supervisor"));
  const careFlagEnabled =
    useFeatureFlagEnabled("enable-care-page") === true || import.meta.env.DEV;
  const showCare =
    careFlagEnabled && hasPrivilegedAccess && ability.can("read", "care");
  const roleNames =
    assignedRoles?.map((assignment) => assignment.role.name) ??
    user?.systemRoles ??
    [];
  const supervises =
    roleNames.includes("lead_mentor") ||
    roleNames.includes("supervisor") ||
    Boolean(user?.is_supervisor);
  const { data: supervisorMentors } = useGetUserSupervisorMentors(
    userId,
    canReadMentorships && supervises && !canManageSupervisorAssignments,
  );
  const hasSupervisedMentors = (supervisorMentors?.mentors?.length ?? 0) > 0;
  const showSupervision = shouldShowSupervision({
    roleNames,
    isSupervisor: user?.is_supervisor,
    canManageSupervisorAssignments,
    canReadMentorships,
    hasSupervisedMentors,
  });

  const isMentor = Boolean(user && isMentorIdentity(user.systemRoles));
  const { data: profileReview } = useMentorProfileReviewByMentorId(
    userId,
    isMentor,
  );
  const profileChangeCount =
    user && profileReview
      ? pendingProfileChangeCount(profileReview, liveProfileDraft(user))
      : 0;
  const tabs = user
    ? getRecordTabs({
        user,
        canReadMentorships,
        canViewRoles,
        showSupervision,
        showCare,
      })
    : [];
  const [activeTabId, setActiveTabId] = useState<UserRecordTabId | null>(
    initialTabId ?? null,
  );
  // the route reuses this component across records and ?tab= changes
  useEffect(() => {
    setActiveTabId(initialTabId ?? null);
  }, [userId, initialTabId]);
  const resolvedTab =
    activeTabId && tabs.some((tab) => tab.id === activeTabId)
      ? activeTabId
      : defaultTabId(tabs);

  if (isPending) {
    return <RecordPageLoader />;
  }

  if (isError || !user) {
    return (
      <p className="p-8 font-body text-sm text-states-error">
        Unable to load this user.
      </p>
    );
  }

  return (
    <UserRecordShell
      user={user}
      surface="record"
      tabs={tabs}
      activeTabId={resolvedTab}
      onTabSelect={setActiveTabId}
      badges={{ matching: profileChangeCount }}
      back={back}
    >
      <UserRecordTabContent
        tabId={resolvedTab}
        user={user}
        canReadMentorships={canReadMentorships}
      />
    </UserRecordShell>
  );
}
